import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { PREFERENCES } from "helpers/apis";
import useAxios from "helpers/axios";
import { IKitStatus, IPreferences } from "types/preference";

import useAuth from "./useAuth";

interface IPreferenceContextType {
  currentKits: IKitStatus[];
  loading: boolean;
  preferences: IPreferences | null;
  setCurrentKits: React.Dispatch<React.SetStateAction<IKitStatus[]>>;
  setPreferences: React.Dispatch<React.SetStateAction<IPreferences | null>>;
  updatePreferences: (
    changes: Partial<IPreferences>,
    callback: () => void
  ) => void;
}

export const PreferenceContext = createContext<IPreferenceContextType>(
  {} as IPreferenceContextType
);

export function PreferenceProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const { isLoggedIn } = useAuth();

  const [preferences, setPreferences] = useState<IPreferences | null>(null);
  const [currentKits, setCurrentKits] = useState<IKitStatus[]>([]);
  // Load disease types
  const { fetchData, loading } = useAxios({
    config: { url: PREFERENCES },
    successCallback: ({ response }: any) => {
      setPreferences(response?.data || []);
      if (Array.isArray(response?.data?.sequencingEstimator)) {
        setCurrentKits(
          response?.data?.sequencingEstimator.filter(
            (kitStatus: IKitStatus) => kitStatus.status === "active"
          )
        );
      } else {
        setCurrentKits([]);
      }
    },
    callOnInit: false,
  });

  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const updatePreferences = useCallback(
    (changes: Partial<IPreferences>, successCallback) => {
      fetchData({ method: "PUT", data: changes }, successCallback);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const memoedValue = useMemo(
    () => ({
      preferences,
      loading,
      currentKits,
      setCurrentKits,
      setPreferences,
      updatePreferences,
    }),
    [preferences, loading, currentKits, updatePreferences]
  );

  return (
    <PreferenceContext.Provider value={memoedValue}>
      {children}
    </PreferenceContext.Provider>
  );
}

export default function usePreference() {
  return useContext(PreferenceContext);
}
